/**
 * Payroll — org-wide monthly sheet, one row per active employee, downloadable as CSV.
 *
 * Two kinds of number meet in this table. Full days, half days, only-in entries, present days
 * and hours are DERIVED from what the cameras saw and are read-only. Leave (unpaid, paid, sick),
 * work-from-home and AI model error are TYPED IN by an administrator — no camera can observe
 * them — and are editable cells that persist per employee per month.
 *
 * Holiday sits in neither group: it is derived, but from the org-wide holiday calendar in Payroll
 * Setup rather than from a camera. It reads as a normal day-count column and is deliberately NOT
 * editable — a holiday applies to everyone, so entering it per employee meant repeating the same
 * number down a column of hundreds of rows and getting it wrong for anyone who joined mid-month.
 * Declaring the dates once lets the server work out who each holiday actually earns a day for.
 *
 * AI model error is the one hand-entered column that is not about absence: it records days the
 * person WAS on site but the recognition pipeline failed to log them, which would otherwise read
 * as absence and cost them a day's pay. Entering it credits the day and keeps the model's misses
 * visible as a number instead of buried in paid leave. Because an outage misses EVERYONE who was
 * on site that day, Payroll Setup can also set it for every employee at once; these cells then
 * adjust the exceptions.
 *
 * Which of those actually earn a payable day is Payroll Setup, not a fixed rule: the modal
 * behind the Payroll setup button owns both the switches and the month's working-day count.
 * The formula and each component's weight come from the API (`payroll_components`) so this
 * page never hardcodes a second copy of the payroll policy.
 *
 * Edits are BATCHED. Typing in a cell updates that row's payable figure immediately but
 * nothing is persisted until Save — payroll numbers shouldn't be committed by a stray blur,
 * and an administrator working down a column wants one deliberate save at the end.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowDownTrayIcon, Cog6ToothIcon, ExclamationTriangleIcon, XMarkIcon, ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import { usePermissions } from '../../auth';
import PayrollSetupModal, {
  effectiveWeight, weightedLabel,
  type PayrollComponent, type PayrollSettings, type PayrollHoliday,
} from './PayrollSetupModal';
import {
  TableCard, TableSkeleton, TableEmpty, Pager, FilterBar, TH, TD, ROW,
  StatTile, StatTiles, usePagination, PageSizeSelect, RECORD_PAGE_SIZE, TableError,
  FILTER_BTN, FilterCount, FilterActions,
} from './tableUi';
import {
  FilterColumn, FilterSection, FilterSearch, FILTER_CONTROL, PageWithFilters, useFilterColumn,
} from '../../components/layout/FilterColumn';

/**
 * Fixed column widths for a sixteen-column sheet, as percentages of the table.
 *
 * `table-fixed` + a colgroup, rather than letting the browser size columns to content: with
 * automatic layout every spare pixel lands in the one unconstrained column, which made
 * Employee enormous while the day counts stayed cramped. Declaring the widths puts the space
 * where it is useful and keeps the columns steady as the numbers change between months.
 *
 * The wide ones are wide because of their HEADER, not their values — "Work from home" over a
 * single digit — so each gets enough room to wrap in two lines rather than being abbreviated.
 * Must sum to 100.
 *
 * Employee is the one column deliberately kept tight. It is the only one whose content can be
 * arbitrarily long, so giving it room enough for the longest name would starve fourteen day-count
 * columns to feed one; it truncates instead, with the full name in the cell's tooltip. The space
 * saved goes to the counts, whose headers are what actually need it.
 *
 * Percentages alone stopped being enough at sixteen columns: on a laptop they divide down to a
 * few characters each, and a header like "AI model error" would wrap to four lines. Hence
 * TABLE_MIN_WIDTH below — under it the sheet scrolls sideways in its card rather than crushing.
 */
const COLUMNS = [
  { key: 'employee', width: '11%' },
  { key: 'month', width: '7%' },
  { key: 'working', width: '6%' },
  { key: 'full', width: '5%' },
  { key: 'half', width: '5%' },
  { key: 'partial', width: '6%' },
  { key: 'only_in', width: '7%' },
  { key: 'present', width: '6%' },
  { key: 'unpaid', width: '6%' },
  { key: 'paid', width: '6%' },
  { key: 'sick', width: '6%' },
  { key: 'holiday', width: '6%' },
  { key: 'wfh', width: '6%' },
  { key: 'ai_error', width: '7%' },
  { key: 'hours', width: '5%' },
  { key: 'payable', width: '5%' },
] as const;

/**
 * The narrowest the sheet is still readable at — sixteen columns, six of them holding an input
 * box. TableCard already scrolls horizontally; this is what gives it something to scroll.
 * A literal Tailwind class, not an interpolated one: the scanner only sees literals.
 *
 * Set by the count columns rather than by Employee, which truncates: at this width the 6–7%
 * columns are ~70–80px, enough for their headers to wrap in two lines over a centred digit.
 */
const TABLE_MIN_WIDTH = 'min-w-[1180px]';

/**
 * Column classes. Derived from the shared TH/TD by substitution rather than by appending
 * `px-2 text-center`: two conflicting Tailwind utilities in one class string are resolved by
 * stylesheet order, not by the order they appear here, so appending would leave the winner to
 * chance. The replacements are written as literal strings, which is what Tailwind scans for —
 * a class assembled from fragments would generate no CSS.
 */
const TIGHT_TH = TH.replace('px-4', 'px-2') + ' align-middle';
const TIGHT_TD = TD.replace('px-4', 'px-2') + ' align-middle';
/**
 * Numeric column, header and cells both CENTRED. Only Employee stays left-aligned: everything
 * to its right is a one or two digit count in a fixed, narrow column, and centring keeps each
 * label sitting over its own numbers — a wrapped two-line header like "Work from home" reads
 * as belonging to that column instead of drifting toward its neighbour.
 *
 * The header is also allowed to wrap: the shared TH is `whitespace-nowrap`, which is what
 * forced these columns wider than their contents. Tighter letter-spacing and `leading-tight`
 * keep a two-line header from towering over the rows, and `align-middle` centres one-line and
 * two-line labels on the same axis so the header row doesn't read as two staggered rows.
 */
const NUM_TH = TIGHT_TH
  .replace('text-left', 'text-center')
  .replace('whitespace-nowrap', 'whitespace-normal')
  .replace('tracking-wider', 'tracking-normal') + ' leading-tight';
/**
 * Cells centre on both axes. Vertically it matters because the row's height is set by its
 * tallest cell — the two-line Employee and Month cells — and a single count would otherwise
 * sit on the first line rather than in the middle of the row it belongs to. `tabular-nums`
 * still matters when centred: it keeps digits from jittering row to row.
 */
const NUM_TD = `${TIGHT_TD} text-center tabular-nums`;

/**
 * Editable day-count cell. Narrow, and with the spinner arrows suppressed — they cost more
 * width than they earn on a keyboard-driven sheet, and `step=0.5` still works from the arrow
 * keys. `appearance: textfield` covers Firefox, the pseudo-elements Chrome and Safari.
 */
const NUM_INPUT = 'w-12 text-center [appearance:textfield] '
  + '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

/** Permission required to change any hand-entered payroll figure or the setup. */
const EDIT_PERMISSION = 'attendance.config';

/** Current month as YYYY-MM, from local date parts — toISOString() can roll back a day. */
function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The columns that are not camera-derived, in the order the table shows them: the three leave
 * kinds together, then holiday and work-from-home, then AI model error last — it sits beside
 * Total hours and Payable because it is a correction to what the pipeline recorded rather than a
 * kind of absence.
 *
 * `derived: true` means the number comes from the holiday calendar in Payroll Setup, so the cell
 * renders as a figure and not an input. One list with a flag rather than two lists: the render
 * order and the set of writable fields would otherwise be free to drift apart, and the failure
 * that produces — a column that looks editable but is never saved — is invisible until payroll
 * day.
 *
 * `field` must match the API's `payroll_components[].field`, which is what ties a column to its
 * Payroll Setup switch and day credit.
 */
const DAY_COLUMNS = [
  { field: 'unpaid_leave_days', label: 'Unpaid leave', derived: false },
  { field: 'paid_leave_days', label: 'Paid leave', derived: false },
  { field: 'sick_leave_days', label: 'Sick leave', derived: false },
  { field: 'holiday_days', label: 'Holiday', derived: true },
  { field: 'wfh_days', label: 'Work from home', derived: false },
  { field: 'ai_error_days', label: 'AI model error', derived: false },
] as const;

/** The writable subset, taken from the one list above so the two can't disagree. */
type EditableField = Extract<(typeof DAY_COLUMNS)[number], { derived: false }>['field'];
const EDITABLE: readonly { field: EditableField; label: string }[] =
  DAY_COLUMNS.filter((c) => !c.derived) as readonly { field: EditableField; label: string }[];

export interface PayrollRow {
  employee_id: string;
  name: string;
  employee_code: string | null;
  department: string | null;
  month: string;
  working_days: number;
  present_days: number;
  full_days: number;
  half_days: number;
  only_in_entry_days: number;
  partial_days: number;
  unpaid_leave_days: number;
  paid_leave_days: number;
  sick_leave_days: number;
  /** Derived from the org-wide holiday calendar, per employee — see PayrollSetupModal. */
  holiday_days: number;
  wfh_days: number;
  /** Days the person was on site but the recognition pipeline never logged them. */
  ai_error_days: number;
  total_hours: number;
  payable_days: number;
  started_on: string | null;
  adjustments_updated_by: string | null;
}

/**
 * Row orderings offered in the filter bar.
 *
 * A dropdown rather than the click-to-sort headers the other attendance tables use
 * (`useSort` / `SortableTH`): this sheet's header is a dense two-line grid of narrow
 * numeric columns with explanatory tooltips, and there is no room for a sort affordance
 * in each without crowding out the labels. Every ordering is offered in both directions,
 * so nothing here is reachable only by reading the list backwards.
 */
const SORTS = {
  name: { label: 'Name (A–Z)', compare: (a: PayrollRow, b: PayrollRow) => a.name.localeCompare(b.name) },
  name_desc: { label: 'Name (Z–A)', compare: (a: PayrollRow, b: PayrollRow) => b.name.localeCompare(a.name) },
  payable: { label: 'Most payable days', compare: (a: PayrollRow, b: PayrollRow) => b.payable_days - a.payable_days },
  lowest: { label: 'Fewest payable days', compare: (a: PayrollRow, b: PayrollRow) => a.payable_days - b.payable_days },
  hours: { label: 'Most hours', compare: (a: PayrollRow, b: PayrollRow) => b.total_hours - a.total_hours },
  hours_low: { label: 'Fewest hours', compare: (a: PayrollRow, b: PayrollRow) => a.total_hours - b.total_hours },
} as const;

type SortKey = keyof typeof SORTS;

/** Day counts read as "21" and "20.5", never "21.0" or a float tail. */
const fmtDays = (n: number) => (Number.isInteger(n) ? String(n) : (Math.round(n * 10) / 10).toFixed(1));

/**
 * A mid-month start date as "20 Jun", for the second line of the Month cell — the year and
 * month are already on the line above it, and the full date is in the cell's tooltip.
 *
 * Split off the string rather than parsed: `new Date('2026-06-20')` is UTC midnight, which is
 * the previous day in any timezone behind UTC.
 */
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtStart(workDate: string): string {
  const [, m, d] = workDate.split('-');
  const name = MONTH_ABBR[Number(m) - 1];
  return name ? `${Number(d)} ${name}` : workDate;
}

/** Minutes as hours for display — "8", "7.5" — since the API stores minutes. */
const fmtHours = (minutes: number) => {
  const h = Math.round((minutes / 60) * 100) / 100;
  return Number.isInteger(h) ? String(h) : String(h);
};

/**
 * Turn an axios failure into something a payroll operator can act on.
 *
 * A 404 here does not mean "employee not found" — the write endpoints live in the node's
 * command-center service, so a missing route means that service is older than this dashboard.
 * Reported plainly instead of surfacing "Request failed with status code 404".
 */
function saveErrorMessage(e: any, fallback: string): string {
  const status = e?.response?.status;
  const serverMessage = e?.response?.data?.message;
  if (status === 404 && !serverMessage) {
    return "The command-center service does not have the payroll endpoints — it needs an updated build.";
  }
  if (status === 403) return 'Your role is not allowed to change payroll figures.';
  return serverMessage || fallback;
}

/** Leave entries are whole or half days; anything finer is a typo, not a payroll figure. */
function isValidDayCount(raw: string, max: number): boolean {
  if (raw.trim() === '') return true; // treated as 0 on commit
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= max && Math.round(n * 2) === n * 2;
}

export interface PayrollTabProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
  onOpenEmployee: (id: string) => void;
}

export default function PayrollTab({ nodeKey = '', onOpenEmployee }: PayrollTabProps) {
  const { attendance } = useApi();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(EDIT_PERMISSION);

  const [month, setMonth] = useState<string>(thisMonth());
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [components, setComponents] = useState<PayrollComponent[]>([]);
  const [settings, setSettings] = useState<PayrollSettings>({});
  const [weightOptions, setWeightOptions] = useState<number[]>([]);
  const [holidays, setHolidays] = useState<PayrollHoliday[]>([]);
  const [meta, setMeta] = useState<{
    working_days?: number;
    auto_working_days?: number;
    working_days_source?: string;
    days_in_month?: number;
    is_partial_month?: boolean;
    /** 'legacy' on a month whose holidays were keyed in before the calendar existed. */
    holiday_days_source?: string;
  }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  /** The "what do these columns mean" notes. Collapsed by default — read once, not every visit. */
  const [notesOpen, setNotesOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const filterCol = useFilterColumn('attendance-payroll');

  /** In-progress cell text, keyed `${employee_id}:${field}`. Absent = untouched. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await attendance.monthlyReport(month);
      setRows(res?.employees || []);
      setComponents(res?.payroll_components || []);
      setSettings(res?.settings || {});
      setWeightOptions(res?.weight_options || []);
      setHolidays(res?.holidays || []);
      setDrafts({});
      setSaveError(null);
      setMeta({
        working_days: res?.working_days,
        auto_working_days: res?.auto_working_days,
        working_days_source: res?.working_days_source,
        days_in_month: res?.days_in_month,
        is_partial_month: res?.is_partial_month,
        holiday_days_source: res?.holiday_days_source,
      });
    } catch (e) {
      console.error('monthly report fetch failed', e);
      setError('Could not load the payroll report.');
      setRows([]);
      setHolidays([]);
      setMeta({});
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, month, nodeKey]);

  useEffect(() => { fetch(); }, [fetch]);

  const cellKey = (employeeId: string, field: EditableField) => `${employeeId}:${field}`;

  /** What a cell currently shows as a number — the draft when valid, else the stored value. */
  const effective = useCallback((row: PayrollRow, field: EditableField): number => {
    const raw = drafts[cellKey(row.employee_id, field)];
    if (raw === undefined) return row[field] ?? 0;
    if (!isValidDayCount(raw, row.working_days)) return row[field] ?? 0;
    return raw.trim() === '' ? 0 : Number(raw);
  }, [drafts]);

  /**
   * The payable formula, driven by the API's components + settings so this page can't hold a
   * stale copy of the policy. The server recomputes on save and its figure wins.
   */
  const payableFor = useCallback((row: PayrollRow): number => {
    const total = components.reduce((sum, c) => {
      if (!settings[c.setting]) return sum;
      const isEditable = EDITABLE.some((e) => e.field === c.field);
      const value = isEditable
        ? effective(row, c.field as EditableField)
        : ((row as any)[c.field] ?? 0);
      return sum + value * effectiveWeight(c, settings);
    }, 0);
    return Math.round(total * 10) / 10;
  }, [components, settings, effective]);

  /** Fields whose draft differs from what's stored, per employee. */
  const dirtyByEmployee = useMemo(() => {
    const out: Record<string, EditableField[]> = {};
    for (const row of rows) {
      const fields = EDITABLE
        .map((c) => c.field)
        .filter((f) => {
          const raw = drafts[cellKey(row.employee_id, f)];
          if (raw === undefined || !isValidDayCount(raw, row.working_days)) return false;
          return (raw.trim() === '' ? 0 : Number(raw)) !== (row[f] ?? 0);
        });
      if (fields.length) out[row.employee_id] = fields;
    }
    return out;
  }, [rows, drafts]);

  const dirtyCount = Object.keys(dirtyByEmployee).length;

  /** Any cell holding text that could never be saved — blocks the save button. */
  const invalidCount = useMemo(() => {
    let n = 0;
    for (const row of rows) {
      for (const c of EDITABLE) {
        const raw = drafts[cellKey(row.employee_id, c.field)];
        if (raw !== undefined && !isValidDayCount(raw, row.working_days)) n += 1;
      }
    }
    return n;
  }, [rows, drafts]);

  const departments = useMemo(
    () => Array.from(new Set(rows.map((r) => r.department).filter(Boolean))).sort() as string[],
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (department && (r.department || '') !== department) return false;
      if (!q) return true;
      return [r.name, r.employee_code, r.department].some((v) => (v || '').toLowerCase().includes(q));
    });
    // Copy before sorting — `rows` is state and Array.sort mutates in place.
    return [...out].sort(SORTS[sort].compare);
  }, [rows, search, department, sort]);

  const { pageItems, pagerProps, pageSizeProps, setPage } = usePagination(filtered, RECORD_PAGE_SIZE);

  // Any filter change starts the reader back at the top of the list.
  useEffect(() => { setPage(1); }, [search, department, sort, month, setPage]);

  // Totals reflect the filtered set, so they always describe the table underneath them.
  const totals = useMemo(() => ({
    payable: filtered.reduce((s, r) => s + payableFor(r), 0),
    leave: filtered.reduce(
      (s, r) => s + effective(r, 'paid_leave_days') + effective(r, 'unpaid_leave_days')
        + effective(r, 'sick_leave_days'),
      0,
    ),
    // Kept out of the leave total: these are days worked, not days away — and the org-wide
    // count is the readable measure of how often the pipeline missed someone.
    aiErrors: filtered.reduce((s, r) => s + effective(r, 'ai_error_days'), 0),
  }), [filtered, payableFor, effective]);

  /** Persist every dirty row, then refetch so the sheet matches the database exactly. */
  const saveChanges = async () => {
    if (!dirtyCount || saving) return;
    setSaving(true);
    setSaveError(null);
    const failures: string[] = [];
    for (const [employeeId, fields] of Object.entries(dirtyByEmployee)) {
      const row = rows.find((r) => r.employee_id === employeeId);
      if (!row) continue;
      const patch: Record<string, number> = {};
      for (const f of fields) patch[f] = effective(row, f);
      try {
        await attendance.setPayrollAdjustments(month, employeeId, patch);
      } catch (e: any) {
        failures.push(`${row.name}: ${saveErrorMessage(e, 'save failed')}`);
      }
    }
    // Refetch either way — some rows may have landed before one failed, and the sheet must
    // show what is actually stored rather than a mix of saved and unsaved numbers.
    await fetch();
    if (failures.length) setSaveError(`${failures.length} row(s) not saved — ${failures.join('; ')}`);
    setSaving(false);
  };

  const saveSetup = async ({
    settings: nextSettings, workingDays, dayMinutes, holidays: holidayDiff, bulkAdjustments,
  }: {
    settings?: Record<string, boolean | number>;
    workingDays?: number | null;
    dayMinutes?: { full_day_minutes: number; half_day_minutes: number };
    holidays?: { added: { date: string; name: string }[]; removed: string[] };
    bulkAdjustments?: Record<string, number>;
  }) => {
    // Refetched whatever happens: these are several independent writes, so a failure part-way
    // must leave the sheet showing what is actually stored rather than a mix of applied and
    // pending changes. The modal stays open on the rethrown error, its draft re-synced from the
    // refetched holidays.
    try {
      // Switches and thresholds live on the same row, so send them together — two PUTs would
      // leave a window where the sheet reflects one change but not the other.
      if (nextSettings || dayMinutes) {
        await attendance.setPayrollSettings({ ...(nextSettings || {}), ...(dayMinutes || {}) });
      }
      if (workingDays !== undefined) await attendance.setPayrollWorkingDays(month, workingDays);
      // One request per date, sequentially. The two sets are disjoint by construction (a renamed
      // date only ever appears in `added`, since declaring upserts on the date), so the order
      // between them doesn't matter; a month holds a handful of holidays, so there is nothing to
      // win by firing them in parallel and losing which one failed.
      for (const date of holidayDiff?.removed || []) await attendance.removePayrollHoliday(date);
      for (const h of holidayDiff?.added || []) await attendance.addPayrollHoliday(h.date, h.name);
      if (bulkAdjustments) await attendance.setPayrollBulkAdjustments(month, bulkAdjustments);
    } finally {
      await fetch();
    }
  };

  const filtersActive = !!search || !!department;

  /**
   * Download the filtered rows as CSV — what's exported is what's on screen, unsaved edits
   * included (the save bar makes it obvious when there are any).
   *
   * Prefixed with a UTF-8 BOM and CRLF-terminated so Excel opens it with non-ASCII names
   * intact instead of mojibake, and every field is quoted so a department like "Ops, North"
   * can't shift the columns.
   */
  const downloadCsv = () => {
    // Camera-derived columns, then the day columns in the table's own order (DAY_COLUMNS), then
    // the totals — so the CSV reads like the sheet and gains a column when the table does.
    const headers = [
      'Employee', 'Code', 'Department', 'Month', 'Working days', 'Full days', 'Half days',
      'Partial days', 'Only-in entries', 'Present', ...DAY_COLUMNS.map((c) => c.label),
      'Total hours', 'Payable days',
    ];
    const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      headers.map(cell).join(','),
      ...filtered.map((r) => [
        r.name, r.employee_code || '', r.department || '', r.month, r.working_days,
        r.full_days, r.half_days, r.partial_days, r.only_in_entry_days, r.present_days,
        // Derived columns export what the server sent; editable ones export what is on screen.
        ...DAY_COLUMNS.map((c) => (c.derived ? (r[c.field] ?? 0) : effective(r, c.field))),
        r.total_hours, payableFor(r),
      ].map(cell).join(',')),
    ];
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-payroll-${month}.csv`;
    a.click();
    // Revoked on the next tick: Safari aborts the download if the blob URL dies
    // synchronously after the click.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  /** Components switched off don't earn credit — say so on the column instead of silently zeroing. */
  const componentEnabled = (field: string) => {
    const c = components.find((x) => x.field === field);
    return c ? !!settings[c.setting] : true;
  };
  const offNote = (field: string) =>
    componentEnabled(field) ? '' : ' — not counted towards payable days (Payroll setup)';

  /** The API's own one-line description of a component, for that column's tooltip. */
  const componentHint = (field: string) => components.find((c) => c.field === field)?.hint || '';

  /**
   * How much one day in a column is worth, for columns not worth a whole day. Stated on the
   * header because a half-credit column otherwise looks like it just isn't adding up.
   */
  const creditNote = (field: string) => {
    const c = components.find((x) => x.field === field);
    if (!c || !componentEnabled(field)) return '';
    const w = effectiveWeight(c, settings);
    return w === 1 ? ' — each day earns a full payable day' : ` — each day earns ${w} of a payable day`;
  };

  /**
   * The report is served by the node's command-center service, which may be older than this
   * dashboard — the frontend and the service are deployed as separate artifacts. Payroll Setup
   * needs `payroll_components`, so its absence means the service predates it: the setup
   * endpoints would 404 and the derived columns would render blank.
   *
   * Detected rather than assumed, so a stale node explains itself instead of showing an
   * inert button and a table of zeroes.
   */
  const setupAvailable = components.length > 0;
  const serviceOutdated = !loading && !error && rows.length > 0 && !setupAvailable;
  const unavailableNote = "The command-center service does not have payroll setup yet";

  /**
   * Holiday figures for a month whose holidays were keyed in per employee before the calendar
   * existed. Worth saying on the page: the Holiday column has numbers in it that the calendar
   * does not explain, and declaring a date for the month replaces them wholesale.
   */
  const legacyHolidays = meta.holiday_days_source === 'legacy'
    && holidays.length === 0
    && rows.some((r) => (r.holiday_days ?? 0) > 0);

  const clearFilters = () => { setSearch(''); setDepartment(''); };
  const activeCount = [!!search, !!department].filter(Boolean).length;

  return (
    <PageWithFilters
      filters={
        <FilterColumn
          open={filterCol.open}
          onToggle={filterCol.toggle}
          label="Payroll filters"
          activeCount={activeCount}
          onClearAll={clearFilters}
        >
          <FilterSection title="Month">
            <input
              data-tour="attendance-payroll-month"
              type="month"
              value={month}
              max={thisMonth()}
              onChange={(e) => setMonth(e.target.value)}
              aria-label="Payroll month"
              className={FILTER_CONTROL}
            />
          </FilterSection>

          <FilterSection title="Search">
            <FilterSearch value={search} onChange={setSearch} placeholder="Name, code, department…" />
          </FilterSection>

          {departments.length > 0 && (
            <FilterSection title="Department">
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                aria-label="Filter by department"
                className={FILTER_CONTROL}
              >
                <option value="">All departments</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FilterSection>
          )}

          <FilterSection title="Sort">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              aria-label="Sort rows"
              className={FILTER_CONTROL}
            >
              {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select>
          </FilterSection>
        </FilterColumn>
      }
    >
      <StatTiles>
        <StatTile
          label="Employees"
          value={filtered.length}
          tone="neutral"
          hint={filtered.length !== rows.length ? `of ${rows.length} total` : undefined}
        />
        <StatTile
          label="Working days"
          value={meta.working_days ?? '—'}
          tone="info"
          hint={meta.working_days_source === 'override'
            ? `set manually (derived ${meta.auto_working_days})`
            : 'calendar days minus Sundays'}
        />
        <StatTile label="Payable days" value={fmtDays(totals.payable)} tone="success" hint="all rows below" />
        <StatTile label="Leave days" value={fmtDays(totals.leave)} tone="muted" hint="paid + unpaid + sick" />
      </StatTiles>

      <FilterBar>
        <FilterCount>
          {filtersActive ? `${filtered.length} of ${rows.length}` : `${rows.length}`} employees
        </FilterCount>
        {filtersActive && (
          <button onClick={clearFilters} className={FILTER_BTN}>
            <XMarkIcon className="h-3.5 w-3.5" /> Clear
          </button>
        )}

        <FilterActions>
          <PageSizeSelect {...pageSizeProps} />
          <button
            data-tour="attendance-payroll-setup"
            onClick={() => setSetupOpen(true)}
            disabled={loading || !setupAvailable}
            title={!setupAvailable
              ? `${unavailableNote} — it needs an updated build`
              : canEdit
                ? 'Working days, holidays, which day types earn a payable day, and AI model error for everyone'
                : 'View the payroll setup (read-only for your role)'}
            className={`${FILTER_BTN} disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <Cog6ToothIcon className="h-4 w-4" /> Setup
          </button>
          <button onClick={fetch} className={FILTER_BTN} aria-label="Refresh">
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            data-tour="attendance-payroll-csv"
            onClick={downloadCsv}
            disabled={filtered.length === 0}
            title={filtersActive ? 'Downloads the filtered rows shown below' : 'Downloads all rows'}
            className={`${FILTER_BTN} bg-brand/10 text-brand hover:bg-brand/20 disabled:cursor-not-allowed disabled:opacity-40`}
            style={{ borderColor: 'transparent' }}
          >
            <ArrowDownTrayIcon className="h-4 w-4" /> CSV
          </button>
        </FilterActions>
      </FilterBar>

      {/* A dashboard newer than the node's service would otherwise present an inert Payroll
          setup button and a table of blanks, with nothing on screen saying why. */}
      {serviceOutdated && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/5 px-4 py-2.5 text-[12px]">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <span className="text-foreground">
            {unavailableNote}. Working days, Full day, Only-in entry and Payable will stay blank
            or zero, and saving leave counts will fail, until the command-center service on this
            node is updated to a build that includes it.
          </span>
        </div>
      )}

      {/* Which numbers are observed and which are typed in is the first question anyone asks
          of a payroll sheet, so it's answered on the page rather than in a tooltip.

          The answer is two things though: the FORMULA, which is checked every time the Payable
          column looks wrong, and the explanation of it, which is read once. Kept as a summary
          line plus a toggle rather than one paragraph — as prose it was six lines of small print
          sitting between the filters and the sheet, which is where it stopped being read. */}
      <div data-tour="attendance-payroll-formula" className="mb-3 rounded-xl border border-border bg-card px-4 py-2.5 text-[12px] shadow-card">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="text-muted-foreground">Payable =</span>
          <span className="font-medium text-foreground">
            {!setupAvailable
              ? 'from the payroll setup'
              : components.filter((c) => settings[c.setting]).length === 0
                ? 'nothing is currently counted'
                : components
                    .filter((c) => settings[c.setting])
                    .map((c) => weightedLabel(c, effectiveWeight(c, settings)))
                    .join(' + ')}
          </span>
          {/* The thresholds decide how the observed days split, so they belong next to the
              formula rather than only inside the modal. */}
          {setupAvailable && settings.full_day_minutes != null && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
              full ≥ {fmtHours(settings.full_day_minutes)}h · half ≥ {fmtHours(settings.half_day_minutes ?? 0)}h
            </span>
          )}
          {setupAvailable && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
              {holidays.length > 0
                ? `${holidays.length} holiday${holidays.length === 1 ? '' : 's'} declared`
                : 'no holidays declared'}
            </span>
          )}
          {/* Gated exactly like the toolbar button — an ungated link here would open a modal
              whose save is guaranteed to fail against an older service. */}
          <div className="ml-auto flex items-center gap-2">
            {setupAvailable && (
              <button onClick={() => setSetupOpen(true)} className="text-brand hover:underline">
                {canEdit ? 'Change setup' : 'View setup'}
              </button>
            )}
            <button
              onClick={() => setNotesOpen((o) => !o)}
              aria-expanded={notesOpen}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              {notesOpen ? 'Hide notes' : 'What do these columns mean?'}
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${notesOpen ? '' : '-rotate-90'}`} />
            </button>
          </div>
        </div>

        {/* Conditions that describe THIS month's figures stay outside the toggle — a sheet whose
            numbers are incomplete or come from a superseded source has to say so unprompted. */}
        {(meta.is_partial_month || totals.aiErrors > 0 || legacyHolidays) && (
          <div className="mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
            {meta.is_partial_month && (
              <p className="text-warning">
                {month} is still in progress, so attendance figures are incomplete.
              </p>
            )}
            {/* Days the cameras should have caught and didn't. Stated on the page because it is a
                measure of the pipeline, not of the employee — and it is only ever visible here. */}
            {totals.aiErrors > 0 && (
              <p>
                {fmtDays(totals.aiErrors)} day{totals.aiErrors === 1 ? '' : 's'} marked as AI model
                error — the person was on site but was not recognised.
              </p>
            )}
            {legacyHolidays && (
              <p>
                No holidays are declared for {month}, so the Holiday figures are the ones entered
                per employee before the calendar existed.
              </p>
            )}
          </div>
        )}

        {notesOpen && (
          <div className="mt-2 space-y-1.5 border-t border-border pt-2 text-[11px] leading-relaxed text-muted-foreground">
            <p>
              <span className="font-medium text-foreground">Observed by the cameras, read-only:</span>{' '}
              full days, half days, partial, only-in entries, present and total hours. Working days
              is the month's calendar days minus Sundays unless it was set in the setup.
            </p>
            <p>
              <span className="font-medium text-foreground">Typed in:</span> unpaid, paid and sick
              leave, work from home, and AI model error — no camera can observe them.{' '}
              {canEdit
                ? 'Edits apply to the Payable column live and are stored when you save.'
                : 'These columns are read-only for your role.'}
            </p>
            {/* The Holiday column has no input, which reads as a bug unless the page says where
                the number comes from and where to change it. */}
            <p>
              <span className="font-medium text-foreground">Holiday</span> is derived but not from a
              camera: it comes from the org-wide holiday calendar
              {setupAvailable ? (
                <>
                  {' '}in{' '}
                  <button onClick={() => setSetupOpen(true)} className="text-brand hover:underline">
                    payroll setup
                  </button>
                </>
              ) : ' in payroll setup'}
              , so it is not editable per employee — the server works out who each holiday actually
              earns a day for.
            </p>
            <p>
              <span className="font-medium text-foreground">AI model error</span> records days
              someone WAS on site but the pipeline never logged them; entering it credits the day
              instead of letting it read as absence. An outage misses everyone on site that day, so
              the setup can set it for every employee at once.
            </p>
          </div>
        )}
      </div>

      {/* Unsaved-changes bar. Sits above the table and sticks to the top of the viewport while
          the sheet scrolls: on a full page of rows the count and the Save button would otherwise
          be somewhere above the cell being edited. */}
      {(dirtyCount > 0 || invalidCount > 0 || saveError) && (
        <div className="sticky top-2 z-20 mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-brand/40 bg-brand/5 px-4 py-2.5 text-[12px] shadow-card backdrop-blur supports-[backdrop-filter]:bg-brand/10">
          {invalidCount > 0 ? (
            <span className="flex items-center gap-1.5 text-warning">
              <ExclamationTriangleIcon className="h-4 w-4" />
              {invalidCount} cell{invalidCount === 1 ? '' : 's'} hold an invalid value — use whole or half days within the working-day count.
            </span>
          ) : (
            <span className="text-foreground">
              {dirtyCount} employee{dirtyCount === 1 ? '' : 's'} changed and not yet saved.
            </span>
          )}
          {saveError && <span className="text-destructive">{saveError}</span>}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => { setDrafts({}); setSaveError(null); }}
              disabled={saving}
              className="rounded-lg border border-border px-3 py-1 text-foreground hover:bg-muted disabled:opacity-40"
            >
              Discard
            </button>
            <button
              onClick={saveChanges}
              disabled={saving || dirtyCount === 0 || invalidCount > 0}
              className="rounded-lg bg-brand px-3 py-1 font-medium text-white hover:bg-brand/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : `Save ${dirtyCount || ''} change${dirtyCount === 1 ? '' : 's'}`.trim()}
            </button>
          </div>
        </div>
      )}

      <TableCard>
        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <TableError onRetry={fetch}>Couldn&apos;t load payroll for {month}</TableError>
        ) : rows.length === 0 ? (
          <TableEmpty>No active employees to report on for {month}.</TableEmpty>
        ) : filtered.length === 0 ? (
          <TableEmpty>No employees match these filters.</TableEmpty>
        ) : (
          <>
            <table className={`w-full table-fixed text-[13px] ${TABLE_MIN_WIDTH}`}>
              <colgroup>
                {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={TIGHT_TH}>Employee</th>
                  <th className={NUM_TH} title="The reported month, and the start date where someone joined part-way through it">
                    Month
                  </th>
                  <th className={NUM_TH} title="Calendar days minus Sundays, or the count set in payroll setup">
                    Working day
                  </th>
                  {/* Camera-derived buckets first, then Present as their subtotal, then the
                      hand-entered columns — so the row reads left to right as it adds up. */}
                  <th className={NUM_TH} title={`Days reaching the full-day hours${offNote('full_days')}`}>Full day</th>
                  <th className={NUM_TH} title={`Days reaching the half-day hours${offNote('half_days')}${creditNote('half_days')}`}>
                    Half day
                  </th>
                  <th
                    className={NUM_TH}
                    title={`Arrival and departure both seen, but under the half-day hours${offNote('partial_days')}${creditNote('partial_days')}`}
                  >
                    Partial
                  </th>
                  <th
                    className={NUM_TH}
                    title={`An entry event but no exit, and under the half-day hours${offNote('only_in_entry_days')}${creditNote('only_in_entry_days')}`}
                  >
                    Only-in entry
                  </th>
                  <th className={NUM_TH} title="Days seen on site at all — the four columns to the left added together">
                    Present
                  </th>
                  {/* The tooltip is the API's own description of the component, so a column
                      like AI model error explains itself here rather than only in the setup
                      modal — and the two can't drift apart. */}
                  {DAY_COLUMNS.map((c) => (
                    <th
                      key={c.field}
                      className={NUM_TH}
                      title={`${componentHint(c.field) || (c.derived ? 'Derived' : 'Entered by hand')}${offNote(c.field)}`}
                    >
                      {c.label}
                    </th>
                  ))}
                  <th className={NUM_TH} title="Recorded time on site this month">Total hours</th>
                  <th className={NUM_TH} title="Calculated from your payroll setup">Payable day</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((r: PayrollRow) => {
                  const dirtyFields = dirtyByEmployee[r.employee_id] || [];
                  const payable = payableFor(r);
                  return (
                    <tr key={r.employee_id} className={`${ROW} ${dirtyFields.length ? 'bg-brand/5' : ''}`}>
                      {/* Fixed-width column, so a long name has to be clipped rather than
                          widening the sheet; the full name is in the title. `min-w-0` is what
                          lets `truncate` engage — a flex item otherwise refuses to shrink
                          below its content. */}
                      <td className={TIGHT_TD}>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onOpenEmployee(r.employee_id)}
                            title={r.name}
                            className="min-w-0 truncate text-left font-medium text-foreground hover:text-brand transition-colors"
                          >
                            {r.name}
                          </button>
                          {dirtyFields.length > 0 && (
                            <span className="shrink-0 text-brand" title="Unsaved changes on this row">•</span>
                          )}
                        </div>
                        <div className="truncate text-xs text-muted-foreground" title={r.department || undefined}>
                          {r.employee_code || r.department || ''}
                        </div>
                      </td>
                      {/* The start date sits under the month it qualifies: a mid-month joiner has
                          no attendance before it, so a low payable figure is expected rather than
                          a data problem — and that only makes sense next to the period. */}
                      <td className={`${NUM_TD} text-muted-foreground`}>
                        {r.month}
                        {r.started_on && (
                          <span
                            className="block whitespace-nowrap text-[11px]"
                            title={`Joined mid-month on ${r.started_on} — no attendance is expected before that date`}
                          >
                            from {fmtStart(r.started_on)}
                          </span>
                        )}
                      </td>
                      <td className={`${NUM_TD} text-muted-foreground`}>{r.working_days}</td>
                      <td className={`${NUM_TD} ${componentEnabled('full_days') ? '' : 'text-muted-foreground line-through'}`}>
                        {r.full_days}
                      </td>
                      <td className={`${NUM_TD} ${componentEnabled('half_days') ? '' : 'text-muted-foreground line-through'}`}>
                        {r.half_days}
                      </td>
                      <td
                        className={`${NUM_TD} ${componentEnabled('partial_days') ? '' : 'text-muted-foreground line-through'}`}
                        title={r.partial_days
                          ? `${r.partial_days} short day(s) with both an arrival and a departure${offNote('partial_days')}`
                          : undefined}
                      >
                        {r.partial_days}
                      </td>
                      <td
                        className={`${NUM_TD} ${componentEnabled('only_in_entry_days') ? '' : 'text-muted-foreground line-through'}`}
                        title={r.only_in_entry_days
                          ? `${r.only_in_entry_days} day(s) with an entry but no exit${offNote('only_in_entry_days')}`
                          : undefined}
                      >
                        {r.only_in_entry_days}
                      </td>
                      <td
                        className={NUM_TD}
                        title={`Full ${r.full_days} + half ${r.half_days} + partial ${r.partial_days} + only-in ${r.only_in_entry_days} = ${r.present_days} days seen`}
                      >
                        {r.present_days}
                      </td>

                      {DAY_COLUMNS.map((c) => {
                        // Holiday: derived from the calendar, so it reads like the camera-derived
                        // counts to its left rather than offering an input nobody can save.
                        if (c.derived) {
                          return (
                            <td
                              key={c.field}
                              className={`${NUM_TD} ${componentEnabled(c.field) ? '' : 'text-muted-foreground line-through'}`}
                              title={`${c.label} for ${r.name} — from the holiday calendar in Payroll setup${offNote(c.field)}`}
                            >
                              {fmtDays(r[c.field] ?? 0)}
                            </td>
                          );
                        }
                        const field = c.field;
                        const key = cellKey(r.employee_id, field);
                        const raw = drafts[key];
                        const invalid = raw !== undefined && !isValidDayCount(raw, r.working_days);
                        const isDirty = dirtyFields.includes(field);
                        // NUM_TD centres the input box in its column, the way the read-only
                        // counts either side of it are centred.
                        return (
                          <td key={c.field} className={NUM_TD}>
                            <input
                              type="number"
                              min={0}
                              max={r.working_days}
                              step={0.5}
                              disabled={!canEdit}
                              value={raw !== undefined ? raw : String(r[c.field] ?? 0)}
                              onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                              title={invalid
                                ? `Enter a whole or half day between 0 and ${r.working_days}`
                                : `${c.label} for ${r.name}${offNote(c.field)}`}
                              aria-label={`${c.label} for ${r.name}`}
                              aria-invalid={invalid || undefined}
                              className={`${NUM_INPUT} rounded border bg-background px-1.5 py-1 text-foreground tabular-nums
                                ${invalid ? 'border-destructive' : isDirty ? 'border-brand' : 'border-border'}
                                ${canEdit ? 'hover:border-brand/60' : 'opacity-60 cursor-not-allowed'}
                                ${componentEnabled(c.field) ? '' : 'text-muted-foreground'}`}
                            />
                          </td>
                        );
                      })}

                      <td className={NUM_TD}>{r.total_hours}</td>
                      <td
                        className={`${NUM_TD} font-semibold ${payable > r.working_days ? 'text-warning' : ''}`}
                        title={payable > r.working_days
                          ? `Exceeds the month's ${r.working_days} working days — check the leave entries`
                          : r.adjustments_updated_by
                            ? `Leave/WFH last set by ${r.adjustments_updated_by}`
                            : undefined}
                      >
                        {fmtDays(payable)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager {...pagerProps} />
          </>
        )}
      </TableCard>

      {setupOpen && (
        <PayrollSetupModal
          month={month}
          workingDays={meta.working_days ?? 0}
          autoWorkingDays={meta.auto_working_days ?? 0}
          workingDaysSource={meta.working_days_source}
          daysInMonth={meta.days_in_month ?? 31}
          components={components}
          settings={settings}
          weightOptions={weightOptions}
          holidays={holidays}
          // Both counts are unfiltered: a bulk apply writes to every active employee, not to
          // whatever subset the filter bar is currently showing. Read from the stored values
          // rather than from `effective`, since unsaved cell edits are not what a bulk write
          // would be overwriting.
          employeeCount={rows.length}
          aiErrorEmployeeCount={rows.filter((r) => (r.ai_error_days ?? 0) > 0).length}
          // Told up front, so a role that may only look at the setup sees read-only controls
          // instead of filling the modal in and being refused by the guard below.
          canEdit={canEdit}
          onSave={canEdit ? saveSetup : async () => { throw new Error('Your role cannot change the payroll setup.'); }}
          onClose={() => setSetupOpen(false)}
        />
      )}
    </PageWithFilters>
  );
}
